// blackhole.js: chapter 3 GRAVITY. Sagittarius A*, ray-traced live.
// A physically based simulation of a non-rotating (Schwarzschild) black hole,
// M = 1 (G = c = 1): null geodesics, a thin Page-Thorne disk with Doppler +
// gravitational redshift and beaming, a lensed star field and Milky Way.
// Everything on screen is a function of (progress, look, params, time).
import * as THREE from 'three';
import { Blit, loadTexture, makeTarget } from './lib/common.js';
import { VERT, DISKTEX_FRAG, SKYDET_FRAG, RAY_FRAG } from './blackhole/shader.js';
import { BH_TIERS, rigAt, pageThorne } from './blackhole/rig.js';

const RIN = 6;            // ISCO
const ROUT = 24;          // disk outer radius (M); emission fades out over the outer half
const TSCALE = 10;        // disk time: M per second of wall clock
const B_C = 3 * Math.sqrt(3);
const UP = new THREE.Vector3(0, 1, 0);
const ZERO_LOOK = { x: 0, y: 0 };

export function create(renderer, opts) {
  const tier = BH_TIERS[opts.tier] ? opts.tier : 'mid';
  const T = BH_TIERS[tier];
  const pnum = (P, k, d) => (P[k] !== undefined ? +P[k] : d);
  const half = opts.half !== false;
  const ENC = half ? 1 : 0.25;            // 8-bit fallback: store HDR / 4, blit x4
  const span = Math.log(ROUT / RIN);
  let fmax = 0;
  for (let r = RIN; r < 40; r += 0.005) fmax = Math.max(fmax, pageThorne(r));

  // BH frame (y = disk axis) -> galactic Cartesian (x -> l = 0, y -> l = 90, z -> NGP).
  // The Galactic Centre sits right behind the hole as seen from the arrival
  // position (so its glow is lensed into an Einstein ring), and the band is
  // rolled so its lensed image crosses the disk on a diagonal. (Artistic: the
  // panorama is the sky as seen from Earth.)
  const skyRot = new THREE.Matrix3();
  function buildSky(el0, az0, roll) {
    const e1 = new THREE.Vector3(-Math.cos(el0) * Math.sin(az0), -Math.sin(el0), -Math.cos(el0) * Math.cos(az0));
    const n = new THREE.Vector3(0, 1, 0).addScaledVector(e1, -e1.y).normalize();
    const b = new THREE.Vector3().crossVectors(e1, n);
    const e3 = n.multiplyScalar(Math.cos(roll)).addScaledVector(b, Math.sin(roll));
    const e2 = new THREE.Vector3().crossVectors(e3, e1);
    skyRot.set(e1.x, e1.y, e1.z, e2.x, e2.y, e2.z, e3.x, e3.y, e3.z);
  }
  // GC ~2.5 deg off the arrival line of sight: asymmetric lensed arcs, not a bullseye
  buildSky(14 * Math.PI / 180, -1.5 * Math.PI / 180, 32 * Math.PI / 180);

  const U = {
    uCamPos: { value: new THREE.Vector3(0, 0, 100) },
    uCamBasis: { value: new THREE.Matrix3() },
    uTanHalf: { value: 0.3 }, uAspect: { value: 1.6 }, uShift: { value: new THREE.Vector2() },
    uK: { value: T.k }, uKin: { value: T.kin }, uHmax: { value: 1e4 }, uResc: { value: 200 },
    uDiskTex: { value: null }, uDiskTexSize: { value: new THREE.Vector2(T.disk[0], T.disk[1]) },
    uTime: { value: 0 }, uRin: { value: RIN }, uRout: { value: ROUT }, uSpan: { value: span },
    uFmaxInv: { value: 1 / fmax }, uTpeak: { value: 4700 }, uBeam: { value: 3.0 },
    uDiskGain: { value: 0.75 }, uTau0: { value: 1.0 }, uIgnite: { value: 1 }, uFront: { value: 60 }, uEmber: { value: 0.06 },
    uPixAng: { value: 0.001 }, uGcam: { value: 1 }, uOrderMask: { value: new THREE.Vector4(1, 1, 1, 1) },
    uMW: { value: null }, uSkyDet: { value: null }, uSkyRot: { value: skyRot }, uMWGain: { value: 0.35 }, uStarGain: { value: 1.0 },
    uStarN: { value: [18, 44, 110, 280] }, uStarF: { value: [2.6, 0.9, 0.3, 0.1] },
    uGain: { value: 1 },
    uKnee: { value: 0.7 }, uBeamSoft: { value: 0.8 }, uHotBlue: { value: 0.8 }, uBlueT: { value: 0.4 }, uAniso: { value: 6 }, uGRamp: { value: new THREE.Vector4(1, 1.2, 0.6, 0.5) }, uPal: { value: 1 }, uRingTint: { value: 0.75 }, uSkyCool: { value: 1 }, uSkyDetail: { value: 1 }, uGrazeSoft: { value: 1 },
    uClose: { value: 0.75 }, uCloseBlur: { value: 2 }, uStreak: { value: 2 }, uStreakK: { value: new THREE.Vector2(8, 32) }, uRingRes: { value: 0.45 }, uRingEps: { value: 1 }, uCloseR: { value: new THREE.Vector2(8, 28) },
  };
  const rayMat = new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: RAY_FRAG, uniforms: U,
    // SKY_FIL: the arrival ring's fine dust filaments (one extra fetch); low keeps the
    // cheap part of the ring sharpening (unsharp glow, crisper boosted star arcs).
    // STAR_RING: star layers run in the ring zone (those past STAR_LAYERS only there)
    defines: { MAX_STEPS: T.steps, STAR_LAYERS: T.stars, MAX_HITS: T.hits, SKY_FIL: tier === 'low' ? 0 : 1, STAR_RING: 4 },
    depthTest: false, depthWrite: false,
  });
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quadGeo = new THREE.PlaneGeometry(2, 2);
  const quad = new THREE.Mesh(quadGeo, rayMat);
  quad.frustumCulled = false;
  scene.add(quad);
  const blit = new Blit({ bicubic: true });

  let rayRT = null, diskRT = null, skyRT = null, mwTex = null, ownMw = false, ready = false;
  let W = 1440, H = 900;

  // scratch (no per-frame allocation)
  const rig = {};
  const pos = new THREE.Vector3(), rh = new THREE.Vector3();
  const fIn = new THREE.Vector3(), right = new THREE.Vector3(), up = new THREE.Vector3(), fwd = new THREE.Vector3();
  const tmpA = new THREE.Vector3(), tmpB = new THREE.Vector3();
  const anchors = {
    center: { x: 0.5, y: 0.5, visible: true },
    shadow: { x: 0.5, y: 0.5, visible: true },
    photonRing: { x: 0.5, y: 0.5, visible: true },
  };
  const view = { tanHalf: 0.3, aspect: 1.6, sx: 0, sy: 0 };
  function project(dir, out) {
    const cz = dir.dot(fwd);
    if (cz <= 1e-4) { out.visible = false; return out; }
    const nx = dir.dot(right) / cz / (view.tanHalf * view.aspect), ny = dir.dot(up) / cz / view.tanHalf;
    out.x = 0.5 + view.sx + nx * 0.5;
    out.y = 1 - (0.5 + view.sy + ny * 0.5);
    out.visible = out.x > -0.02 && out.x < 1.02 && out.y > -0.02 && out.y < 1.02;
    return out;
  }

  function bakeDisk() {
    const [dw, dh] = T.disk;
    diskRT = bake(DISKTEX_FRAG, dw, dh, { uSpan: { value: span } }, THREE.ClampToEdgeWrapping);
    U.uDiskTex.value = diskRT.texture;
    const sd = T.sky;
    skyRT = bake(SKYDET_FRAG, sd, sd, {}, THREE.RepeatWrapping);
    U.uSkyDet.value = skyRT.texture;
  }

  // one-off procedural texture: fragment shader -> RGBA8 target with mipmaps
  function bake(frag, w, h, uniforms, wrapT) {
    const rt = new THREE.WebGLRenderTarget(w, h, {
      type: THREE.UnsignedByteType, depthBuffer: false, generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter,
      wrapS: THREE.RepeatWrapping, wrapT,
    });
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: frag, uniforms,
      depthTest: false, depthWrite: false,
    });
    const s = new THREE.Scene();
    const m = new THREE.Mesh(quadGeo, mat);
    m.frustumCulled = false;
    s.add(m);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(rt);
    renderer.render(s, camera);       // three regenerates the mip chain after the draw
    renderer.setRenderTarget(prev);
    mat.dispose();
    // anisotropic filtering: the disk is mostly seen at grazing angles
    const caps = renderer.capabilities;
    rt.texture.anisotropy = Math.min(8, caps.getMaxAnisotropy ? caps.getMaxAnisotropy() : 1);
    return rt;
  }

  function blackTex() {
    const t = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
    t.needsUpdate = true;
    return t;
  }

  // ------------------------------------------------------------------ targets
  function sizeRay() {
    const pr = renderer.getPixelRatio ? renderer.getPixelRatio() : 1;
    const cssW = W / pr, cssH = H / pr;
    let s = T.res * (+api.params.res || 1);
    const px = cssW * cssH * s * s;
    if (px > T.maxPx) s *= Math.sqrt(T.maxPx / px);
    const w = Math.max(64, Math.round(cssW * s)), h = Math.max(64, Math.round(cssH * s));
    if (rayRT && rayRT.width === w && rayRT.height === h) return;
    if (rayRT) rayRT.setSize(w, h);
    else rayRT = makeTarget(w, h, { half });
    U.uAspect.value = w / h;
  }

  const api = {
    scene, camera,
    params: {},
    grade: {
      exposure: 1.0, bloomK: 0.85, threshold: 0.9, sat: 1.2, contrast: 1.08,   // r14: sat 1.1 -> 1.2 (C >= 40 on the hero)
      vignette: 0.42, grain: 0.04, ca: 0.0012,
      highs: [0.55, 0.5, 1.0], shadows: [0.07, 0.05, 0.2],   // r14: cool lavender highs, violet-navy shadows gainC: [1, 1, 1],
    },
    anchors,

    async load() {
      U.uMW.value = blackTex();
      U.uDiskTex.value = U.uMW.value;
      U.uSkyDet.value = U.uMW.value;
      try { bakeDisk(); } catch (e) { console.warn('[blackhole] disk bake failed', e); }
      try {
        const url = `${opts.base || ''}assets/blackhole/${T.mw}`;
        const t = await Promise.race([
          loadTexture(url, { srgb: true, repeat: true, renderer }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('mw timeout')), 20000)),
        ]);
        t.wrapT = THREE.ClampToEdgeWrapping;
        mwTex = t; ownMw = true;
        U.uMW.value = t;
      } catch (e) { console.warn('[blackhole] sky texture unavailable', e && e.message); }
      ready = true;
    },

    resize(w, h) {
      W = Math.max(1, w | 0); H = Math.max(1, h | 0);
      sizeRay();
    },

    update(dt, time, input) {
      const params = (input && input.params) || {};
      api.params = params;
      const p = Math.min(1, Math.max(0, (input && input.progress) || 0));
      const aspect = (input && input.aspect) || W / H;
      rigAt(p, (input && input.look) || ZERO_LOOK, params, aspect, rig);

      // camera position (BH frame, y = disk axis) and orientation
      const ce = Math.cos(rig.el), se = Math.sin(rig.el);
      pos.set(ce * Math.sin(rig.az), se, ce * Math.cos(rig.az)).multiplyScalar(rig.r);
      rh.copy(pos).normalize();
      fIn.copy(rh).negate();
      tmpA.crossVectors(fIn, UP).normalize();           // right when looking at the hole
      tmpB.crossVectors(tmpA, fIn).normalize();         // up when looking at the hole
      // the dive: pitch away from the hole (frame-up = away from the hole)
      const ang = rig.beta;
      fwd.copy(fIn).multiplyScalar(Math.cos(ang)).addScaledVector(tmpB, Math.sin(ang));
      up.copy(tmpB).multiplyScalar(Math.cos(ang)).addScaledVector(fIn, -Math.sin(ang));
      right.copy(tmpA);
      const cr = Math.cos(rig.roll), sr = Math.sin(rig.roll);
      tmpB.copy(right).multiplyScalar(cr).addScaledVector(up, sr);
      up.multiplyScalar(cr).addScaledVector(right, -sr);
      right.copy(tmpB);
      U.uCamPos.value.copy(pos);
      U.uCamBasis.value.set(right.x, up.x, fwd.x, right.y, up.y, fwd.y, right.z, up.z, fwd.z);

      sizeRay();
      U.uTanHalf.value = rig.tanHalf;
      U.uPixAng.value = 2 * rig.tanHalf / rayRT.height;
      // portrait phones: compose the subject in the upper-middle (copy sits below)
      const sx = +params.shiftX || 0, sy = (+params.shiftY || 0) + (aspect < 1 ? 0.07 : 0);
      U.uShift.value.set(sx, sy);
      U.uResc.value = Math.max(1.25 * rig.r, 40);
      // deep in the well accuracy matters less than cost: open the inner step up
      U.uKin.value = T.kin + (T.k - T.kin) * Math.min(1, Math.max(0, (8 - rig.r) / 5));
      const gcam = 1 / Math.sqrt(Math.max(1 - 2 / rig.r, 1e-6));
      U.uGcam.value = gcam;
      U.uTime.value = (time || 0) * TSCALE * (params.speed !== undefined ? +params.speed : 1);
      U.uIgnite.value = rig.ignite;
      U.uFront.value = rig.front;
      U.uMWGain.value = (0.055 + 0.3 * rig.sky) * (params.mw !== undefined ? +params.mw : 1);
      U.uStarGain.value = (1 + 0.4 * rig.sky) * (params.stars !== undefined ? +params.stars : 1);
      if (params.disk !== undefined) U.uDiskGain.value = +params.disk;
      if (params.beam !== undefined) U.uBeam.value = +params.beam;
      U.uKnee.value = pnum(params, 'knee', 0.7); U.uBeamSoft.value = pnum(params, 'beamSoft', 0.8);
      U.uHotBlue.value = pnum(params, 'hotBlue', 0.8); U.uBlueT.value = pnum(params, 'blueT', 0.4);
      U.uAniso.value = pnum(params, 'aniso', 6); U.uGrazeSoft.value = pnum(params, 'graze', 1); U.uSkyDetail.value = pnum(params, 'skyDetail', 1) * rig.sky;   // arrival only
      U.uClose.value = pnum(params, 'close', 0.75); U.uCloseBlur.value = pnum(params, 'closeBlur', 2); U.uStreak.value = pnum(params, 'streak', 2); U.uStreakK.value.set(Math.round(pnum(params, 'streakU', 8)), pnum(params, 'streakV', 32));
      // ring-zone star resolve limit: low renders coarser pixels, so it resolves further
      U.uRingRes.value = Math.max(0.3, pnum(params, 'ringRes', tier === 'low' ? 1.0 : 0.45));
      U.uRingEps.value = Math.max(1, pnum(params, 'ringEps', tier === 'low' ? 40 : 1));
      U.uCloseR.value.set(pnum(params, 'closeNear', 8), pnum(params, 'closeFar', 28));
      // r14 Doppler palette (LIGHT.md GRAVITY): pal 0 = the old blackbody look (A/B)
      U.uPal.value = pnum(params, 'pal', 1); U.uRingTint.value = pnum(params, 'ringTint', 0.75); U.uSkyCool.value = pnum(params, 'skyCool', 1);
      const gMid = Math.max(0.3, pnum(params, 'gMid', 1.0));
      U.uGRamp.value.set(gMid, Math.max(gMid * 1.02, pnum(params, 'gBlue', 1.2)), Math.min(gMid * 0.98, pnum(params, 'gRed', 0.6)), pnum(params, 'lumaComp', 0.5));
      const om = params;
      U.uOrderMask.value.set(om.o0 !== undefined ? +om.o0 : 1, om.o1 !== undefined ? +om.o1 : 1,
        om.o2 !== undefined ? +om.o2 : 1, om.o3 !== undefined ? +om.o3 : 1);
      // auto-exposure against the g^3 blueshift of a hovering observer deep in the well
      U.uGain.value = ENC * rig.fade / Math.pow(gcam, 2.4);

      // anchors (screen 0..1 from the top-left)
      view.tanHalf = rig.tanHalf; view.aspect = U.uAspect.value; view.sx = sx; view.sy = sy;
      project(fIn, anchors.center);
      anchors.shadow.x = anchors.center.x; anchors.shadow.y = anchors.center.y;
      anchors.shadow.visible = anchors.center.visible && rig.turn < 0.5;
      const sa = Math.min(1, B_C * Math.sqrt(Math.max(1 - 2 / rig.r, 0)) / rig.r);
      const ca = Math.sqrt(1 - sa * sa);
      tmpB.copy(up).multiplyScalar(Math.cos(0.7)).addScaledVector(right, Math.sin(0.7));
      tmpB.multiplyScalar(sa).addScaledVector(fIn, ca);
      project(tmpB, anchors.photonRing);
      anchors.photonRing.visible = anchors.photonRing.visible && rig.turn < 0.3 && rig.r > 6;

      // grade: open the bloom a little as the ring grows, then close in for the fall
      const g = api.grade;
      g.exposure = 1.0;
      g.vignette = 0.42 + 0.3 * rig.turn;
      g.bloomK = 0.9 + 0.3 * rig.turn;
    },

    render(target) {
      if (!rayRT) sizeRay();
      const prevAuto = renderer.autoClear;
      renderer.setRenderTarget(rayRT);
      renderer.render(scene, camera);
      blit.render(renderer, rayRT, target, 1 / ENC);
      renderer.autoClear = prevAuto;
    },

    dispose() {
      if (rayRT) rayRT.dispose();
      if (diskRT) diskRT.dispose();
      if (skyRT) skyRT.dispose();
      if (mwTex && ownMw) mwTex.dispose();
      rayMat.dispose(); quadGeo.dispose(); blit.dispose();
      rayRT = diskRT = skyRT = mwTex = null;
    },
  };
  return api;
}
